import { useCallback, useEffect, useRef, useState } from 'react';
import {
	NAVIGABLE_ACCORDION_SECTION_CLASS_NAME,
	NAVIGABLE_SECTION_IDS,
	SCROLL_TO_NAVIGABLE_ACCORDION_SECTION_EVENT,
} from '@/components/content/ProductDetails/constants';
import { useNavigableSection } from '@/components/content/ProductDetails/hooks/useNavigableSection';
import { getElementOffsetTop } from '@/components/content/ProductDetails/utils';

type ScrollToNavigableAccordionSectionEvent = CustomEvent<{
	sectionId: NAVIGABLE_SECTION_IDS;
	topAdjustment: number;
}>;

const EXPADED_ANIMATION_TIMEOUT = 200;

/**
 * Registers an accordion section as navigable and adds support for programmatic
 * scrolling and automatic expansion.
 *
 * This hook builds on `useNavigableSection` by:
 * - Tracking the expanded/collapsed state of the accordion.
 * - Listening for a custom scroll-to-section event.
 * - Expanding the accordion if needed before scrolling to it.
 * - Adjusting scroll position after the expand animation completes.
 *
 * @param sectionId - The unique identifier for the navigable accordion section.
 * @returns Props that should be spread onto the accordion section's root element,
 *          including navigation metadata, expansion state, and transition handlers.
 */
export const useNavigableAccordionSection = ({
	sectionId,
}: {
	sectionId: NAVIGABLE_SECTION_IDS;
}) => {
	const { className, ref, ...props } = useNavigableSection({ sectionId });

	const [expanded, setExpanded] = useState(false);

	const onChange = useCallback(() => {
		setExpanded((prev) => !prev);
	}, []);

	const pendingScrollTopAdjustmentRef = useRef<number | null>(null);

	// Called when accordion expand animation completes
	const onExpandedAnimationEnd = useCallback((transitionNode: HTMLElement) => {
		const topAdjustment = pendingScrollTopAdjustmentRef.current;
		if (topAdjustment === null) return;

		const sectionElement = transitionNode.closest<HTMLElement>(
			`.${NAVIGABLE_ACCORDION_SECTION_CLASS_NAME}`
		);

		if (sectionElement) {
			scrollToElement({ element: sectionElement, topAdjustment });
		}

		pendingScrollTopAdjustmentRef.current = null;
	}, []);

	// Listen for custom scroll-to-navigable-accordion-section event
	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		const onScrollToAccordionSectionEvent = (e: Event) => {
			const customEvent = e as ScrollToNavigableAccordionSectionEvent;

			if (customEvent.detail.sectionId !== sectionId) return;

			const topAdjustment = customEvent.detail.topAdjustment;

			if (expanded) {
				// If the accordion section is already expanded, scroll to the top of the section.
				scrollToElement({ element, topAdjustment });
			} else {
				// If the accordion section is not expanded, expand it and wait for the animation to complete.
				// Then scroll to the top of the section.
				pendingScrollTopAdjustmentRef.current = topAdjustment;
				setExpanded(true);
			}
		};

		element.addEventListener(
			SCROLL_TO_NAVIGABLE_ACCORDION_SECTION_EVENT,
			onScrollToAccordionSectionEvent
		);

		return () => {
			element.removeEventListener(
				SCROLL_TO_NAVIGABLE_ACCORDION_SECTION_EVENT,
				onScrollToAccordionSectionEvent
			);
		};
	}, [ref, sectionId, expanded]);

	return {
		...props,
		ref,
		className: `${className} ${NAVIGABLE_ACCORDION_SECTION_CLASS_NAME}`,
		expanded,
		onChange,
		slotProps: {
			transition: {
				onEntered: onExpandedAnimationEnd,
				timeout: EXPADED_ANIMATION_TIMEOUT,
			},
		},
	};
};

export { NAVIGABLE_SECTION_IDS };

const scrollToElement = ({
	element,
	topAdjustment,
}: {
	element: HTMLElement;
	topAdjustment: number;
}) => {
	window.scroll({
		top: getElementOffsetTop(element) - topAdjustment,
		behavior: 'smooth',
	});
};
